import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router';

import { useAppSelector as useSelector } from 'dux/app/hooks';

import { styled } from '@prose-ui/legacy';

import { Button } from 'Components/LegacyButton';
import { MetaData } from 'Components/MetaData';
import { headerHeight } from 'Components/ThemeProvider';

import { ProductsCarousel } from 'Blocks/ProductsCarousel';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import content from 'assets/content/socialImpact';

import { getSocialImpactProductsCarouselContent } from 'dux/products/selectors';
import { useFetchProductsList } from 'dux/products/hooks';

import CarbonFootprintBlock from './CarbonFootPrintBlock';
import CertificateBlock from './CertificateBlock';
import EventsBlock from './EventsBlock';
import LocalPartnersBlock from './LocalPartnersBlock';
import ReportBlock from './ReportBlock';
import SocialImpactHero from './SocialImpactHero';
import SocialImpactTextImage from './SocialImpactTextImage';
import StickyNavBarBlock from './StickyNavBarBlock';

const menuHeight = 64;
const scrollMarginTopSize = headerHeight + menuHeight - 1;
// consider section as being in view when its top reaches the bottom of the navbar
const rootMargin = `-${scrollMarginTopSize + 1}px`;

const NavigatedSection = styled.section`
  width: 100%;
  scroll-margin-top: ${scrollMarginTopSize}px;
`;

const StyledProductsCarousel = styled(ProductsCarousel)`
  padding: 75px 0px;
`;

const SocialImpact = () => {
  const { isLoaded } = useFetchProductsList();
  const productsCarouselCards = useSelector(getSocialImpactProductsCarouselContent);

  const { ref: planetRef, inView: planetInView } = useInView({ rootMargin });
  const { ref: communityRef, inView: communityInView } = useInView({ rootMargin });
  const { ref: peopleRef, inView: peopleInView } = useInView({ rootMargin });

  const impactedSections = {
    planet: {
      id: content.planetTextImage.id,
      label: content.planetTextImage.subTitle,
      isInView: planetInView,
    },
    community: {
      id: 'community',
      label: 'community',
      isInView: communityInView,
    },
    people: {
      id: 'people',
      label: 'people',
      isInView: peopleInView,
    },
  };

  const navBarSectionOrder = ['planet', 'community', 'people'];

  // always select the section that is higher on the screen
  const sectionInViewId = navBarSectionOrder.find(section => impactedSections[section]?.isInView);

  return (
    <>
      <MetaData
        description="Prose is committed to build a more sustainable future for our planet and its people. Read our sustainability and social impact report."
        title="Sustainability and Social Impact | Prose"
      />

      <BaseScene footer header>
        <SocialImpactHero
          backgroundImages={content.heroBlock.backgroundImages}
          description={content.heroBlock.description}
          logos={content.heroBlock.logos}
          title={content.heroBlock.title}
        />
        <StickyNavBarBlock
          navbarHeight={menuHeight}
          navBarSeparator="|"
          secionInViewId={sectionInViewId}
          sectionsInfo={navBarSectionOrder.map(sectionName => impactedSections[sectionName])}
        >
          <NavigatedSection
            ref={planetRef}
            data-testid={impactedSections.planet.id}
            id={impactedSections.planet.id}
          >
            <SocialImpactTextImage
              description={content.planetTextImage.description}
              images={content.planetTextImage.images}
              subTitle={content.planetTextImage.subTitle}
              title={content.planetTextImage.title}
            />
            <CarbonFootprintBlock content={content.carbonFootprintBlock} />
            <CertificateBlock
              bCorp={content.certificateBlock.bCorp}
              climateNeutral={content.certificateBlock.climateNeutral}
            />
          </NavigatedSection>
          <NavigatedSection
            ref={communityRef}
            data-testid={impactedSections.community.id}
            id={impactedSections.community.id}
          >
            <SocialImpactTextImage
              description={content.communityTextImage.description}
              images={content.communityTextImage.images}
              subTitle={content.communityTextImage.subTitle}
              title={content.communityTextImage.title}
            />
            <EventsBlock list={content.eventsBlock.list} title={content.eventsBlock.title} />
            <LocalPartnersBlock />
          </NavigatedSection>
          <NavigatedSection
            ref={peopleRef}
            data-testid={impactedSections.people.id}
            id={impactedSections.people.id}
          >
            <SocialImpactTextImage
              description={content.peopleTextImage.description}
              imageCaption={content.peopleTextImage.imageCaption}
              images={content.peopleTextImage.images}
              subTitle={content.peopleTextImage.subTitle}
              title={content.peopleTextImage.title}
            >
              <Button
                Component={Link}
                data-click="Careers"
                data-from="People-Module"
                to={content.peopleTextImage.button.link}
                variant="whiteFull"
              >
                {content.peopleTextImage.button.label}
              </Button>
            </SocialImpactTextImage>
            <ReportBlock button={content.reportBlock.button} title={content.reportBlock.title} />
            {isLoaded && (
              <StyledProductsCarousel
                background="beige"
                content={{ ...content.productsCarousel, cards: productsCarouselCards }}
                margins="0px auto"
                style={{
                  padding: '75px 24px',
                }}
              />
            )}
          </NavigatedSection>
        </StickyNavBarBlock>
      </BaseScene>
    </>
  );
};

export default SocialImpact;
